import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService, RedirectAuthService, AppConfigService } from '@alfresco/adf-core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { MenuService } from '../menu/menu.service';
import { TenantLoader } from '../sector/tenant-loader.service';
import { MenuItemDto, ExpiryBadgeConfig, EXPIRY_BADGE_CONFIG, ExpiryWarningLevel } from '../menu/menu.model';
import { SectorService } from '../sector/sector.service';
import { WfPermissionService } from '../../wf-admin/services/wf-permission.service';
@Component({
  selector: 'sp-header',
  templateUrl: './sp-header.component.html',
  styleUrls: ['./sp-header.component.scss']
})
export class SpHeaderComponent implements OnInit, OnDestroy {

  userDisplayName = 'User';
  userInitials    = 'U';
  showUserMenu    = false;

  /** Dynamically loaded from MenuService */
  headerItems: MenuItemDto[] = [];

  /** Count of tasks needing this user (assigned + claimable) — shown as a badge
      on the My Work nav button. Refreshed on each header init (the header is
      re-created per page navigation), so it reflects current pending work. */
  myWorkCount = 0;

  /** Fingerprint mismatch warning */
  fingerprintWarning = false;

  /** License expiry state */
  expiryWarningLevel: ExpiryWarningLevel = 'NONE';
  daysUntilExpiry    = -1;
  expiryDate: string | null = null;
  expiryBadge: ExpiryBadgeConfig = EXPIRY_BADGE_CONFIG['NONE'];

  /** True when this user's session is view-only (concurrent license seats exhausted). */
  readOnly = false;

  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private redirectAuthService: RedirectAuthService,
    private menuService: MenuService,
    private tenantLoader: TenantLoader,
    public  sector: SectorService,
    private http: HttpClient,
    private appConfig: AppConfigService,
    private permission: WfPermissionService
  ) {}

  /** Help is for admins/implementers only — hide the Help icon from ordinary users. */
  get canSeeHelp(): boolean {
    return this.permission.isAdmin();
  }

  ngOnInit(): void {
    this.loadUserInfo();
    this.loadMyWorkCount();
    document.addEventListener('click', this.handleDocumentClick);

    this.menuService.loadMenu();
    this.tenantLoader.loadTenant();

    this.menuService.headerItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => { this.headerItems = items; });

    this.menuService.fingerprintWarning$
      .pipe(takeUntil(this.destroy$))
      .subscribe(warn => { this.fingerprintWarning = warn; });

    this.menuService.accessMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(mode => { this.readOnly = mode === 'READ_ONLY'; });

    this.menuService.expiryWarningLevel$
      .pipe(takeUntil(this.destroy$))
      .subscribe(level => {
        this.expiryWarningLevel = level;
        this.expiryBadge = EXPIRY_BADGE_CONFIG[level];
      });

    this.menuService.daysUntilExpiry$
      .pipe(takeUntil(this.destroy$))
      .subscribe(d => { this.daysUntilExpiry = d; });

    this.menuService.expiryDate$
      .pipe(takeUntil(this.destroy$))
      .subscribe(d => { this.expiryDate = d; });
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.handleDocumentClick);
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Returns the badge label with {d} replaced by actual days */
  get expiryLabel(): string {
    if (!this.expiryBadge?.label) return '';
    if (this.expiryWarningLevel === 'EXPIRED') return this.expiryBadge.label;
    return this.expiryBadge.label.replace('{d}', String(this.daysUntilExpiry));
  }

  /** True if a full-width banner should show (EXPIRED only) */
  get showExpiredBanner(): boolean {
    return this.expiryWarningLevel === 'EXPIRED';
  }

  openIntelligence(): void {
    this.router.navigate(['/intelligence']);
  }
  /** True if a badge should show in the header (any non-NONE level) */
  get showExpiryBadge(): boolean {
    return this.expiryWarningLevel !== 'NONE' && !this.showExpiredBanner;
  }

  private loadUserInfo(): void {
    try {
      // Prefer the person's FIRST name from the token (given_name / first word of name),
      // not the login id — "Hi, Victor" reads better than "Hi, vmutisya".
      let given = '', family = '', full = '';
      const token = this.authService.getToken?.();
      if (token) {
        const parts = token.split('.');
        if (parts.length >= 2) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          given = payload?.given_name || '';
          family = payload?.family_name || '';
          full = payload?.name || '';
        }
      }
      const firstName = given
        || (full ? full.trim().split(/\s+/)[0] : '')
        || this.authService.getEcmUsername()
        || 'User';
      this.userDisplayName = firstName;

      const initialsSource = (given && family) ? `${given} ${family}` : (full || firstName);
      this.userInitials = initialsSource.split(/\s+/).map((p: string) => p.charAt(0))
        .join('').toUpperCase().substring(0, 2) || 'U';
    } catch (e) { /* silent */ }
  }

  isActive(route: string | null): boolean {
    if (!route) return false;
    return this.router.url === route || this.router.url.startsWith(route + '/');
  }

  /** Locked (TEASE) module the user clicked — drives the upsell modal. Null when closed. */
  teaserItem: MenuItemDto | null = null;

  onNavClick(item: MenuItemDto): void {
    if (item.locked) { this.teaserItem = item; return; }   // showroom: upsell, don't navigate
    if (item.route) this.router.navigate([item.route]);
  }

  closeTeaser(): void { this.teaserItem = null; }

  private handleDocumentClick = (event: Event): void => {
    if (!(event.target as HTMLElement).closest('.sp-user-menu')) {
      this.showUserMenu = false;
    }
  };

  toggleUserMenu(event: Event): void {
    event.stopPropagation();
    this.showUserMenu = !this.showUserMenu;
  }

  goToProfile(): void { this.showUserMenu = false; this.router.navigate(['/profile']); }
  goToSearch():  void { this.router.navigate(['/search']); }

  /** Open the SkyPlus Help (grouped HTML help with full-text search) in a new tab.
   *  URL is configurable via app.config.json `skyplus.helpUrl`; defaults to the help
   *  bundled with the app under assets/help/. */
  openHelp(): void {
    const url = this.appConfig.get<string>('skyplus.helpUrl', 'assets/help/index.html');
    window.open(url, '_blank', 'noopener');
  }
  goToMyWork():  void { this.router.navigate(['/my-work']); }

  /**
   * Cheap pending-work count for the My Work badge. Uses size=1 on both
   * endpoints and reads {@code totalElements} (the row payload is discarded),
   * so it is light enough to run on every header init. Assigned-to-me and
   * group-claimable queues are disjoint (a task assigned to someone is no
   * longer claimable), so summing the two totals gives the count of items
   * waiting on this user. Failures leave the badge hidden rather than erroring.
   */
  private loadMyWorkCount(): void {
    const base = `${this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api')}/wf`;
    const token = this.authService.getToken();
    let headers = new HttpHeaders();
    if (token) { headers = headers.set('Authorization', `Bearer ${token}`); }
    const params = new HttpParams().set('page', '0').set('size', '1');
    forkJoin({
      mine:  this.http.get<{ totalElements: number }>(`${base}/tasks/my`,        { headers, params }),
      claim: this.http.get<{ totalElements: number }>(`${base}/tasks/claimable`, { headers, params }),
      // Sub-tasks (consultations / distributed / FYI) live in wf_sub_task and are
      // NOT part of the instance-task queues above, so the badge missed them. The
      // backend gates inclusion via skyplus.wf.mytasks.count.include-subtasks.
      subtasks: this.http.get<{ count: number }>(`${base}/subtasks/my/count`, { headers })
        .pipe(catchError(() => of({ count: 0 })))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ mine, claim, subtasks }) => {
          this.myWorkCount = (mine?.totalElements || 0) + (claim?.totalElements || 0)
            + (subtasks?.count || 0);
        },
        error: () => { this.myWorkCount = 0; }
      });
  }

  logout(): void {
    this.showUserMenu = false;
    this.menuService.clearMenu();
    this.redirectAuthService.logout();
  }
}